import {
  isIncidentCancelled,
  isIncidentDeleted,
} from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-types'
import { IncidentsResultConfig } from '@desktop/views/Relation/Sports/configuration'

import { resolveIncidentTeamId } from './resolveIncidentTeamId'
import { Result } from './types'

type BuildIncidentResultsMapParams = {
  incidents: Incident[]
  homeTeamId: number
  awayTeamId: number
  incidentsResultConfig: IncidentsResultConfig
}

export function buildIncidentResultsMap({
  incidents,
  homeTeamId,
  awayTeamId,
  incidentsResultConfig,
}: BuildIncidentResultsMapParams): Map<number, Result> {
  const results = new Map<number, Result>()
  const { scoringIncidents, oppositeTeamScoringIncidents } =
    incidentsResultConfig
  let [homeTeamScore, awayTeamScore] = [0, 0]

  for (const incident of incidents) {
    if (isIncidentCancelled(incident) || isIncidentDeleted(incident)) continue

    const scoringIncidentConfig = scoringIncidents.get(incident.incident_id)
    const oppositeTeamScoringIncidentConfig = oppositeTeamScoringIncidents?.get(
      incident.incident_id,
    )

    const setHomeTeamScore = scoringIncidentConfig?.set
    const setAwayTeamScore = oppositeTeamScoringIncidentConfig?.set

    if (setHomeTeamScore !== undefined) {
      homeTeamScore = setHomeTeamScore
    }
    if (setAwayTeamScore !== undefined) {
      awayTeamScore = setAwayTeamScore
    }

    const incidentAttributes = incident.attribute_ids
    const scorePoints = scoringIncidentConfig?.add ?? 0
    const opponentScorePoints = oppositeTeamScoringIncidentConfig?.add ?? 0

    const teamId = resolveIncidentTeamId(incident)
    const isHomeTeamIncident = teamId === homeTeamId
    const isAwayTeamIncident = teamId === awayTeamId

    if (scorePoints) {
      if (isHomeTeamIncident) homeTeamScore += scorePoints
      else if (isAwayTeamIncident) awayTeamScore += scorePoints
    } else if (opponentScorePoints) {
      if (isHomeTeamIncident) awayTeamScore += opponentScorePoints
      else if (isAwayTeamIncident) homeTeamScore += opponentScorePoints
    }

    if (incidentAttributes) {
      for (const attributeId of incidentAttributes) {
        const attributeScoreConfig =
          scoringIncidentConfig?.attributes?.get(attributeId)
        const oppositeTeamAttributeScoreConfig =
          oppositeTeamScoringIncidentConfig?.attributes?.get(attributeId)

        const attributeScorePoints = attributeScoreConfig?.add ?? 0
        const oppositeTeamAttributeScorePoints =
          oppositeTeamAttributeScoreConfig?.add ?? 0

        if (attributeScorePoints) {
          if (isHomeTeamIncident) homeTeamScore += attributeScorePoints
          else if (isAwayTeamIncident) awayTeamScore += attributeScorePoints
        } else if (oppositeTeamAttributeScorePoints) {
          if (isHomeTeamIncident)
            awayTeamScore += oppositeTeamAttributeScorePoints
          else if (isAwayTeamIncident)
            homeTeamScore += oppositeTeamAttributeScorePoints
        }
      }
    }

    results.set(incident.id, {
      firstTeamResult: homeTeamScore,
      secondTeamResult: awayTeamScore,
    })
  }

  return results
}
